import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Input, Label } from 'reactstrap';
import EnumInput from '../../../../../misc/EnumInput';
import NullableInput from '../../../../../misc/NullableInput';
import { AzureAdExportGroupSelectionOptions, AzureAdExportGroupTypeOptions, AzureAdGroupMembershipTypeOptions, CoreAzureAdGroupExport, EntraIdGroupRoleNameLanguage } from '../../../../../../models/CoreAzureAdGroupModels';
import InformationMarkingsInput from '../../components/InformationMarkingsInput';
import i18n from 'i18next';
import MultiSelectInput from '../../../../../misc/MultiSelectInput';
import { DutyRole } from '../../../../../../models/DutyRole';
import { OptionItem, SearchableOptionItem } from '../../../../../../util/UtilityTypes';
import SelectInput from '../../../../../misc/SelectInput';
import NullableIntInputCheckboxText from '../../../../../misc/NullableIntInputCheckboxText';


export const validator = (configuration: CoreAzureAdGroupExport, active: boolean) => {
    const validation = [];

    if ((configuration.inactivateAfter === undefined) || (configuration.inactivateAfter !== null && configuration.inactivateAfter < 0)) {
        validation.push('inactivateAfter');
    }
    if (configuration.removeAfter !== null && configuration.removeAfter < 0) {
        validation.push('removeAfter');
    }

    if (configuration.prefix === '' || (configuration.prefix && configuration.prefix.length > 64)) {
        validation.push('prefix');
    }
    if (configuration.suffix === '' || (configuration.suffix && configuration.suffix.length > 64)) {
        validation.push('suffix');
    }

    if (active) {
        if (configuration.memberMembershipType !== 1 && configuration.memberMembershipType !== 2) {
            validation.push('memberMembershipType')
        }
        if (configuration.ownerMembershipType !== 1 && configuration.ownerMembershipType !== 2) {
            validation.push('ownerMembershipType')
        }
        if (configuration.groupType !== 1 && configuration.groupType !== 2) {
            validation.push('groupType')
        }
        if (configuration.groupSelection <= 0 && configuration.schoolUnitRoleStaffGroupRoles.length === 0) {
            validation.push('groupSelection')
        }
    }
    return validation;
}

export const warning = (originalConfiguration: CoreAzureAdGroupExport, updatedConfiguration: CoreAzureAdGroupExport) => {
    const warnings: string[] = [];

    if (updatedConfiguration.groupSelection) {
        var propsValue = originalConfiguration.groupSelection;

        // Create warning if any group selection is removed but do not create warning if added.
        // XOR to find differance then use AND to check if diff was caused by removing from old value.
        if (((propsValue ^ updatedConfiguration.groupSelection) & propsValue) !== 0) {
            warnings.push(i18n.t('entraIdGroups.groupSelection.warningMessage', { ns: 'configuration' }));
        }
    }

    if (updatedConfiguration.schoolUnitRoleStaffGroupRoles) {
        // If any of the previously configured values is not found in the updated list it should be treated as a removed groupType.
        for (let i = 0; i < originalConfiguration.schoolUnitRoleStaffGroupRoles.length; i++) {
            const configuredValue = originalConfiguration.schoolUnitRoleStaffGroupRoles[i];
            if (-1 === updatedConfiguration.schoolUnitRoleStaffGroupRoles.indexOf(configuredValue)) {
                warnings.push(i18n.t('entraIdGroups.schoolUnitRoleStaffGroupRoles.warningMessage', { ns: 'configuration' }));
                break;
            }
        }
    }

    return warnings;
}

interface AzureAdGroupExportConfigurationProps {
    configuration: CoreAzureAdGroupExport
    disabled: boolean
    updatePart: (part: Partial<CoreAzureAdGroupExport>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}


const AzureAdGroupExportConfiguration = (props: AzureAdGroupExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common', 'schoolAdministration']);

    const [open, setOpen] = useState<boolean>(true);

    const updateAzureAD = async (value?: number | null) => {
        if (value === null) {
            return props.updatePart({ inactivateAfter: value, removeAfter: null })
        }
        props.updatePart({ inactivateAfter: value })
    }

    const dutyRoleItems: SearchableOptionItem<DutyRole>[] = Object.values(DutyRole)
        .filter((v: string | DutyRole): v is DutyRole => typeof v === 'number' && v !== 0)
        .map((dr: DutyRole) => ({
            value: dr,
            label: t(`dutyRoleTypes.${dr}`, { ns: 'schoolAdministration' })
        }));

    const roleSchoolStaffGroupLanguages: OptionItem<EntraIdGroupRoleNameLanguage>[] = [
        {
            value: EntraIdGroupRoleNameLanguage.Swedish,
            text: t('entraIdGroups.roleShoolStaffGroupsLanguage.languages.swedish', { ns: 'configuration' })
        },
        {
            value: EntraIdGroupRoleNameLanguage.English,
            text: t('entraIdGroups.roleShoolStaffGroupsLanguage.languages.english', { ns: 'configuration' })
        }
    ]

    const validation = validator(props.configuration, props.active)

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.AzureADGroupExport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <EnumInput
                            title={t('entraIdGroups.groupType.title', { ns: 'configuration' })}
                            name='groupType'
                            value={props.configuration.groupType}
                            options={AzureAdExportGroupTypeOptions.map(keyvalue => ({ value: keyvalue, text: t(`entraIdGroups.groupType.options.${keyvalue}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('groupType') !== -1}
                            invalidFeedback={t('entraIdGroups.groupType.invalidfeedback', { ns: 'configuration' })}
                        />

                        <EnumInput
                            title={t('entraIdGroups.memberMembershipType.title', { ns: 'configuration' })}
                            name='memberMembershipType'
                            value={props.configuration.memberMembershipType}
                            options={AzureAdGroupMembershipTypeOptions.map(keyValue => ({ value: keyValue, text: t(`entraIdGroups.memberShipOptions.${keyValue}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('memberMembershipType') !== -1}
                            invalidFeedback={t('entraIdGroups.memberMembershipType.invalidfeedback', { ns: 'configuration' })}
                        />

                        <EnumInput
                            title={t('entraIdGroups.ownerMembershipType.title', { ns: 'configuration' })}
                            value={props.configuration.ownerMembershipType}
                            name='ownerMembershipType'
                            options={AzureAdGroupMembershipTypeOptions.map(keyValue => ({ value: keyValue, text: t(`entraIdGroups.memberShipOptions.${keyValue}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('ownerMembershipType') !== -1}
                            invalidFeedback={t('entraIdGroups.ownerMembershipType.invalidfeedback', { ns: 'configuration' })}
                        />

                        <EnumInput
                            multi={true}
                            title={t('entraIdGroups.groupSelection.title', { ns: 'configuration' })}
                            name="groupSelection"
                            value={props.configuration.groupSelection}
                            options={AzureAdExportGroupSelectionOptions.map(keyValue => ({ value: keyValue, text: t(`entraIdGroups.groupSelection.options.${keyValue}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('groupSelection') !== -1}
                            invalidFeedback={t('entraIdGroups.groupSelection.invalidfeedback', { ns: 'configuration' })}
                        />

                        <FormGroup>
                            <h5>{t('entraIdGroups.schoolUnitRoleStaffGroupRoles.title', { ns: 'configuration' })}</h5>
                            <p>{t('entraIdGroups.schoolUnitRoleStaffGroupRoles.description', { ns: 'configuration' })}</p>
                            <MultiSelectInput
                                items={dutyRoleItems}
                                onChange={(_, value) => props.updatePart({
                                    schoolUnitRoleStaffGroupRoles: value ?? []
                                })}
                                defaultText={t('entraIdGroups.schoolUnitRoleStaffGroupRoles.placeholder', { ns: 'configuration' })}
                                name={"rolebasedschoolstaffgroups"}
                                selected={props.configuration.schoolUnitRoleStaffGroupRoles}
                                invalid={validation.indexOf('groupSelection') !== -1}
                                invalidFeedback={t('entraIdGroups.schoolUnitRoleStaffGroupRoles.invalidfeedback', { ns: 'configuration' })}
                                disabled={props.disabled}
                            />

                            <h6>{t('entraIdGroups.roleShoolStaffGroupsLanguage.title', { ns: 'configuration' })}</h6>
                            <p>{t('entraIdGroups.roleShoolStaffGroupsLanguage.description', { ns: 'configuration' })}</p>
                            <SelectInput
                                items={roleSchoolStaffGroupLanguages}
                                onChange={(_, value) => props.updatePart({
                                    groupRoleNameLanguage: value
                                })}
                                selected={props.configuration.groupRoleNameLanguage}
                                name="rolebasedschoolstaffgroupslanguage"
                                defaultText={t('entraIdGroups.roleShoolStaffGroupsLanguage.placeholder', { ns: 'configuration' })}
                                disableDefaultOption={true}
                            />
                        </FormGroup>

                        <InformationMarkingsInput
                            selectedValues={props.configuration.activityInformationMarkingFilters}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            name='activityInformationMarkingFilters'
                        />

                        <FormGroup>
                            <h5>{t('entraIdGroups.affix.header', { ns: 'configuration' })}</h5>
                            <NullableInput
                                disabled={props.disabled}
                                title={t('entraIdGroups.affix.prefix.title', { ns: 'configuration' })}
                                titleSmall={true}
                                checkboxTitle={t('entraIdGroups.affix.prefix.checkbox', { ns: 'configuration' })}
                                value={props.configuration.prefix}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                name="prefix"
                                invalidFeedback={t('entraIdGroups.affix.prefix.invalidfeedback', { ns: 'configuration' })}
                                invalid={validation.indexOf('prefix') !== -1}
                            />
                            <NullableInput
                                disabled={props.disabled}
                                title={t('entraIdGroups.affix.suffix.title', { ns: 'configuration' })}
                                titleSmall={true}
                                checkboxTitle={t('entraIdGroups.affix.suffix.checkbox', { ns: 'configuration' })}
                                value={props.configuration.suffix}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                name="suffix"
                                invalidFeedback={t('entraIdGroups.affix.suffix.invalidfeedback', { ns: 'configuration' })}
                                invalid={validation.indexOf('suffix') !== -1}
                            />
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('entraIdGroups.lifeCycleManagement.header', { ns: 'configuration' })}</h5>
                            <NullableIntInputCheckboxText
                                name="inactivateAfter"
                                value={props.configuration.inactivateAfter}
                                disabled={props.disabled}
                                title={t('entraIdGroups.lifeCycleManagement.inactivateAfter.title', { ns: 'configuration' })}
                                invalidFeedback={t('entraIdGroups.lifeCycleManagement.inactivateAfter.invalidFeedback', { ns: 'configuration' })}
                                invalid={validation.indexOf('inactivateAfter') !== -1}
                                defaultValue={0}
                                onChange={(name, value) => updateAzureAD(value)}
                                checkboxTitle={t('entraIdGroups.lifeCycleManagement.inactivateAfter.checkbox', { ns: 'configuration' })}
                            />

                            <NullableIntInputCheckboxText
                                name="removeAfter"
                                value={props.configuration.removeAfter}
                                disabled={props.disabled || props.configuration.inactivateAfter === null}
                                title={t('entraIdGroups.lifeCycleManagement.removeAfter.title', { ns: 'configuration' })}
                                invalidFeedback={t('entraIdGroups.lifeCycleManagement.removeAfter.invalidFeedback', { ns: 'configuration' })}
                                invalid={validation.indexOf('removeAfter') !== -1}
                                defaultValue={180}
                                onChange={(name, value) => props.updatePart({ removeAfter: value })}
                                checkboxTitle={t('entraIdGroups.lifeCycleManagement.removeAfter.checkbox', { ns: 'configuration' })}
                            />
                        </FormGroup>
                    </div>
                </CardBody>
            </Collapse>
        </Card>)
}

export default AzureAdGroupExportConfiguration;
